/**
 * The LightsOS design document.
 *
 * THE ONE RULE: a mockup is data, never pixels. Everything on screen is drawn
 * from this object by a pure renderer. Never store a flattened image as the
 * project. Every future feature (server-side rendering, version history,
 * automatic footage, AI roofline detection, quoting) depends on this holding.
 *
 * All point coordinates are NORMALIZED 0..1 against the photo, so a saved
 * project survives any change in image size or export resolution.
 */

import type { PricingSettings } from './pricing'
import { defaultPricing } from './pricing'
import type { CorrectionLog, RoofProposal } from './proposal'
import type { RoofSection } from '../lib/sections'
import type { ApprovedScope } from '../lib/approval'
import type { Visit } from '../lib/visits'
import type { QuoteLines } from '../lib/quote'
import type { Paper } from '../lib/paper'
import { defaultPaper } from '../lib/paper'

/**
 * 4 adds the confirmation layer: proposals[], which is where roof geometry
 * lives until a person agrees to it, and which buildQuote never reads.
 *
 * 5 adds sections[]: a place on the house, distinct from the lights drawn on
 * it, carrying a stable reference and a measurement that knows how it was
 * arrived at. Purely additive - a document written at 4 opens at 5 with an
 * empty list and behaves exactly as it did. See lib/sections.ts.
 *
 * 6 adds approvals[]: a frozen copy of the scope somebody agreed to, appended
 * and never edited, so that what was agreed in September survives an estimator
 * redrawing the garage in October. Also additive. See lib/approval.ts.
 */
export const SCHEMA_VERSION = 6

export interface Point {
  x: number
  y: number
}

/** Real product families, with real world dimensions so calibration can drive them. */
export type ProductId = 'C9' | 'C7' | 'mini' | 'permanent'

export type StrandKind =
  | 'roofline' // straight runs along a fascia, gutter or ridge
  | 'freeform' // hand drawn over bushes and irregular shapes
  | 'wrap' // spiral around a trunk or column
  | 'swag' // hangs between two points, sags under its own weight
  // A strand folded in half and hung from a branch, so it reads as two rows
  // of bulbs rather than one thin line. Sold per cluster, not per foot.
  | 'cluster'
  /*
   * A STRAND RUN OUT ALONG ONE PALM FROND.
   *
   * Its own kind and not a bush, which is what it was for one commit. A bush
   * is an AREA: freeform scatters its bulbs into a band around the stroke and
   * lays a mound of glow under them, because that is how a netted shrub reads.
   * Do that to a palm and every leaf becomes a glowing blob.
   *
   * A frond is a LINE, the same as a fascia is, and the only difference from a
   * roofline is what is behind it: dark busy leaves rather than a flat wall.
   * So it renders as a line of lamps with the wider halo foliage needs, and
   * without the wash band that a roofline throws onto the surface under it,
   * because a leaf in mid air has no surface under it.
   */
  | 'frond'

export interface Strand {
  id: string
  type: 'strand'
  kind: StrandKind
  name: string
  visible: boolean

  /**
   * WHO PUT THIS LINE HERE.
   *
   * Written from schema 4 onward, and the only thing a migration is allowed to
   * read when deciding whether old geometry may be treated as customer
   * approved. Absent means unknown, and unknown is never treated as manual.
   */
  origin?: 'manual' | 'confirmed'

  /** Normalized path, in draw order. */
  points: Point[]

  product: ProductId
  /**
   * Which product photo this run is made of, for the kinds that are a real
   * physical thing rather than a bare string of bulbs. Garland is tiled from
   * a photograph of the actual garland along the path you draw.
   */
  styleId?: string
  /** Up to five colors, cycled along the strand. */
  palette: string[]
  /** How many bulbs of each color before moving to the next. 1 = 1x1, 2 = 2x2. */
  patternRepeat: number

  /** Multipliers on the product defaults, so the tech can taste-adjust. */
  bulbScale: number
  spacingScale: number
  brightness: number

  /** 0 = taut, 1 = heavy hang. Only meaningful for swag and wrap. */
  sag: number

  /**
   * WHERE THE GARLAND IS, expressed as how it hangs. Only for kind 'swag'.
   *
   * Garland goes on a house exactly three ways, and one setting cannot serve
   * them: screwed flush to a garage header or door frame; hung in repeating
   * scallops along a porch rail or a fascia; or spiraled round a post or
   * column. The run remembers its own, so a flush garage and a swagged porch
   * rail can live in the same mockup without one of them being wrong.
   */
  garlandShape?: 'flush' | 'swags' | 'wrapped'
  /** Scallops along the run. Only for garlandShape 'swags'. */
  swagCount?: number

  /**
   * Perspective. Relative bulb size at the first and last point of the run.
   * A roofline receding from the camera might be depthStart 1, depthEnd 0.6.
   * Spacing scales with it too, which is what stops a run looking pasted on.
   */
  depthStart: number
  depthEnd: number

  /**
   * Trunk wrapping. Only meaningful for kind 'wrap' and 'cluster'.
   * For a wrap it is the trunk diameter as a fraction of the photo width; for
   * a cluster it drives how far apart the two folded rails hang. Turns is how
   * many times a wrap goes around over the length of the run.
   */
  wrapWidth: number
  wrapTurns: number
  /** Light the fronds at the top as well as the trunk. */
  wrapCap: boolean
  /** The cap can be a different color from the trunk, which is how it is sold. */
  capPalette: string[]
  /**
   * Frond length as a fraction of the photo width, and how many are lit.
   * A Christmas palm has short stiff fronds and a royal has long loose ones,
   * so this has to be per tree rather than a constant.
   */
  capLength: number
  capFronds: number
  /** How far the fronds arch over. 0 is stiff and upright, 1 is heavy droop. */
  capDroop: number
}

export type DecorationType = 'wreath' | 'bow' | 'garland' | 'custom'

export interface Decoration {
  id: string
  type: 'decoration'
  kind: DecorationType
  name: string
  visible: boolean
  /** Normalized center. */
  x: number
  y: number
  /** Width scale. For garland this is how far the piece stretches. */
  scale: number
  /**
   * Height scale. Undefined means "match the width", which is what a wreath
   * always wants. Garland needs the two apart: a run over a garage door gets
   * longer without getting fatter, and a fat garland is the fastest way to
   * make a mockup look fake.
   */
  scaleY?: number
  /**
   * Extra bulbs drawn on top of the artwork, 0..1.
   *
   * The lamp count is baked into a photograph, and the products are not all
   * shot at the same density: the multicolor wreath carries about forty large
   * C9s where the warm white carries a hundred small ones. Rather than send
   * every asset back to be reshot, the renderer adds its own on the same ring,
   * in that product's own colors.
   */
  extraBulbs?: number
  rotation: number
  palette: string[]
  /** Which product photo this is, from the decoration library. */
  styleId?: string
  /** User supplied artwork, for anything not in the library. */
  assetDataUrl?: string
}

export type Layer = Strand | Decoration

export interface SceneSettings {
  /** 0 = the photo as shot, 1 = full night conversion. */
  night: number
  /** How far light washes onto the surface behind a strand. The realism cue. */
  spill: number
  /** Bloom radius multiplier. */
  glow: number
  /** How hard the lights wash the surface they are mounted on. */
  bounce: number

  /**
   * Lens star flares on the brightest bulbs. 0 is off, which is the default:
   * done badly this is the clearest tell of a faked mockup, so it is a
   * deliberate choice rather than something that arrives switched on.
   */
  sparkle?: number

  /**
   * WHICH sparkle. Four different real causes, not four decorations:
   *   cross   a stopped-down lens with straight aperture blades: 4 spikes
   *   star    a 6-blade aperture: 6 spikes, the Christmas-card look
   *   bloom   no spikes at all, just a hotter halo on the brightest lamps
   *   twinkle a few lamps caught noticeably brighter than their neighbors
   */
  sparkleStyle?: 'cross' | 'star' | 'bloom' | 'twinkle'

  /**
   * HOW MANY, separately from how big.
   *
   * These were one dial, and they pull in opposite directions: the size that
   * looks right is generous and the count that looks right is mean. Tying them
   * together meant a sparkle big enough to read only arrived alongside far too
   * many of them. 0 is one glint on a long run; 1 is about one bulb in six.
   */
  sparkleAmount?: number
  /** Crop the export to 16:9 so every proposal image is the same shape. */
  crop169: boolean

  /* How the night conversion is graded. No single look suits every house. */
  /** How far the house, ground and planting come down. */
  nightDarkness: number
  /** How deep the sky goes, independently of the house. */
  nightSky: number
  nightContrast: number
  nightColor: number
  /**
   * Which blue hour treatment. Dusk is the default and the one that sells the
   * job: the homeowner has to recognize their own house while the lights are
   * plainly the brightest thing on it.
   */
  nightPreset: 'dusk' | 'night' | 'darknight'
  /**
   * Developer only: show the graded LUMINANCE alone.
   *
   * The most useful test there is. If the gray version does not already read
   * as evening, the tones still say noon and no amount of color will fix it -
   * so color only gets judged after this passes.
   */
  nightMono?: boolean
}

export interface Calibration {
  /** Null until the user marks a known length. */
  pixelsPerFoot: number | null
  /** Normalized endpoints of the reference measurement, kept so it can be edited. */
  refA: Point | null
  refB: Point | null
  knownFeet: number | null
}

export interface PhotoRef {
  dataUrl: string
  width: number
  height: number
}

export type BrandCorner = 'tl' | 'tr' | 'bl' | 'br'

/**
 * Company branding burned into every export. Set it once, never think about it
 * again. A mockup that gets forwarded around a neighborhood should say who
 * made it.
 */
export interface Branding {
  logoDataUrl: string | null
  corner: BrandCorner
  /** Logo width as a fraction of the photo width. */
  scale: number
  opacity: number
  show: boolean
}

export interface Takeoff {
  /** Overage added to the linear footage when ordering material. */
  wastePct: number

  /*
   * HOW THIS MATERIAL IS ACTUALLY BOUGHT, which is not the same for both.
   *
   * Roof lights come off a big spool: you buy WIRE by the foot and cut it,
   * and you choose the socket spacing, which is what decides how many bulbs
   * that footage swallows. Six, twelve and eighteen inches are the three in use.
   *
   * Mini lights come in finished strands - 23.5 ft is the standard - so trees
   * and bushes are counted in whole strands, not feet.
   *
   * Getting this wrong is not a rounding error, it is turning up at a job
   * short of bulbs, so both are settings rather than assumptions.
   */
  /** Socket spacing on the roof spool, in inches. */
  roofSpacingInches: number
  /** Length of one finished mini-light strand, in feet. */
  miniStrandFeet: number

  /**
   * CORRECT THE FOOTAGE FOR PERSPECTIVE. OFF BY DEFAULT.
   *
   * A run that recedes from the camera really does cover more feet than its
   * pixels suggest, and lib/geometry can correct for it. What it cannot do is
   * know whether THIS run recedes. It used to be applied to every confirmed
   * run out of a default - 10.25% on everything, measured at 9.5 ft on one
   * house in the end-to-end test, on an eave that is nearly parallel to the
   * camera.
   *
   * So it is a decision now, not a default, and the quote calls its footage an
   * estimate either way.
   */
  depthCorrection: boolean
}

/**
 * WHERE A JOB IS IN THE SEASON.
 *
 * A mockup tool that only remembers pictures is a toy. What a contractor
 * actually needs to know in November is which of the forty houses he drew are
 * still waiting on an answer, which he won, and which he can stop chasing.
 * That is one field, and it is the difference between a gallery and a pipeline.
 */
export type JobStatus = 'draft' | 'quoted' | 'won' | 'lost'

/*
 * The ids are the old ones on purpose: a hundred saved jobs already carry
 * them and renaming a state is not worth a migration. The words are what a
 * contractor actually says about a job, which is not the same vocabulary.
 */
export const JOB_STATUS: { id: JobStatus; label: string }[] = [
  { id: 'draft', label: 'Not sent yet' },
  { id: 'quoted', label: 'Not approved yet' },
  { id: 'won', label: 'Approved' },
  { id: 'lost', label: 'Lost' },
]

/**
 * What a migration did, kept on the document so the person can be asked about
 * it once rather than silently having old geometry priced for them.
 */
export interface MigrationRecord {
  fromVersion: number
  at: string
  /** Roof runs moved out of the priced collection and into proposals. */
  legacyRoofRuns: number
  /** True until the contractor has said what those runs are. */
  requiresReview: boolean
  reviewedAt?: string
}

export interface LightsOsDocument {
  schemaVersion: number
  id: string
  /**
   * The company this job belongs to. Absent on everything saved before
   * accounts existed, which is read as belonging to whoever is here now. See
   * lib/account.ts.
   */
  accountId?: string
  name: string
  /** Where it stands. Defaults to 'draft' on anything saved before this. */
  status: JobStatus
  /**
   * WHEN SOMEBODY LAST MOVED IT, and nothing more than that.
   *
   * The one honest answer this product can give to how long has this been
   * sitting. It is not a delivery receipt: opening a share sheet is not a
   * message sent and this app has never claimed otherwise. It is the moment a
   * person marked the job, which is a real decision a real person made, and it
   * is what a list of quotes waiting on an answer is actually sorted by.
   *
   * Absent on everything saved before this existed, and read as unknown rather
   * than as today, because a hundred old jobs all dated now would put every
   * one of them at the top of that list.
   */
  statusAt?: string
  /**
   * Who the job is for. A season is a hundred houses, and three weeks later
   * the only thing anyone remembers is the name and the street.
   */
  customer: string
  address: string
  createdAt: string
  photo: PhotoRef | null
  scene: SceneSettings
  calibration: Calibration
  branding: Branding
  takeoff: Takeoff
  /** Rate card and line overrides. The quote is derived, this is what steers it. */
  pricing: PricingSettings
  /**
   * THE PRICED COLLECTION. buildQuote walks this, and only this.
   *
   * From schema 4 on, no roof run reaches it except through a confirmation.
   */
  layers: Layer[]
  /**
   * Roof geometry waiting on a person. Never priced, never counted, never
   * read by buildQuote. See lib/proposals.ts.
   */
  proposals: RoofProposal[]
  /**
   * THE PLACES ON THIS HOUSE, as opposed to the lights drawn on them.
   *
   * Optional, because every job saved before schema 5 has none and must open
   * unchanged. A section holds a stable reference, a label that can change
   * without moving that reference, the run ids that belong to it, and a
   * measurement that says whether anybody has actually been to the house.
   * See lib/sections.ts.
   */
  sections?: RoofSection[]
  /**
   * WHAT SOMEBODY AGREED TO, FROZEN, IN ORDER.
   *
   * Append only: approving again adds a second scope rather than replacing the
   * first, so the history of what was agreed and when is never lost. The last
   * one is the one that stands. Optional for the same reason sections is.
   * See lib/approval.ts.
   */
  approvals?: ApprovedScope[]
  /**
   * WHEN SOMEBODY IS COMING, AND WHETHER IT IS AGREED.
   *
   * The first genuinely writable record in this product: everything else that
   * looks like a status here is computed off what happened, and cannot
   * disagree with the job. Nothing implies the date a crew arrives, so a
   * person decides it and the decision is the fact. See lib/visits.ts for the
   * three rules it carries - no silent timezone conversion, a preference is
   * not a booking, and a conflict check that cannot check everything says so.
   *
   * Optional for the same reason sections is: every job saved before this
   * existed opens without one.
   */
  visits?: Visit[]
  /** What the customer changed, for later measurement. Null until consented. */
  corrections: CorrectionLog | null
  /** Set when this document was opened from an older schema. */
  migration?: MigrationRecord
  /**
   * THE QUOTE, AS TYPED.
   *
   * Optional, so every document saved before this one loads without a
   * migration and simply has no quote yet. Nothing derives it from the
   * drawing: see lib/quote.ts for why.
   */
  quote?: QuoteLines
  /**
   * THE HEADER, THE LADDER AND THE DEPOSIT.
   *
   * Optional for the same reason the quote is: an older save has none and
   * loads without a migration. See lib/paper.ts.
   */
  paper?: Paper
}

export const defaultBranding = (): Branding => ({
  logoDataUrl: null,
  corner: 'br',
  scale: 0.16,
  opacity: 0.9,
  show: true,
})

export const defaultTakeoff = (): Takeoff => ({
  wastePct: 10,
  roofSpacingInches: 12,
  miniStrandFeet: 23.5,
  depthCorrection: false,
})

export const defaultScene = (): SceneSettings => ({
  // Off by default. A photo should open exactly as it was shot. Turning it to
  // night is a choice you make with a button, not something that happens to
  // your picture the moment you upload it.
  night: 0,
  spill: 0.85,
  glow: 1,
  bounce: 0.85,
  sparkle: 0,
  sparkleStyle: 'star',
  sparkleAmount: 0.3,
  crop169: false,
  nightDarkness: 0.5,
  nightSky: 0.62,
  nightContrast: 0.7,
  nightColor: 0.55,
  nightPreset: 'dusk',
  nightMono: false,
})

export const defaultCalibration = (): Calibration => ({
  pixelsPerFoot: null,
  refA: null,
  refB: null,
  knownFeet: null,
})

export const createDocument = (id: string): LightsOsDocument => ({
  schemaVersion: SCHEMA_VERSION,
  id,
  name: 'Untitled mockup',
  status: 'draft',
  customer: '',
  address: '',
  createdAt: new Date().toISOString(),
  photo: null,
  scene: defaultScene(),
  calibration: defaultCalibration(),
  branding: defaultBranding(),
  takeoff: defaultTakeoff(),
  pricing: defaultPricing(),
  layers: [],
  proposals: [],
  sections: [],
  approvals: [],
  corrections: null,
  quote: [],
  paper: defaultPaper(),
})

export const isStrand = (l: Layer): l is Strand => l.type === 'strand'
export const isDecoration = (l: Layer): l is Decoration => l.type === 'decoration'
